import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Notebook, ArrowSquareOut, ArrowLeft, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";

import {
    defaultPrototype, getPrototype, upsertPrototype,
} from "./builderStorage";
import { aggregateScopeByProduct } from "./productAggregation";
import { materializeBuilderToLineItems, annualCostGaps } from "./materializeBuilder";
import {
    hydrateProductMasterFromCatalogue, getHydrationState, subscribeToHydration, PRODUCT_MASTER,
} from "./productMasterHierarchy";
// 2026-07-23 HOTFIX — Restore CB-02 reverse-materialisation so legacy
// proposals (versions persisted before builder_state was introduced)
// hydrate their Builder from `line_items` instead of showing an empty
// shell. Do NOT remove without full Proposal Versioning v2 regression.
import { reverseMaterializeFromLineItems } from "./reverseMaterialize";

import ProductSelectionSection from "./sections/ProductSelectionSection";
import CommercialSection from "./sections/CommercialSection";
import TaxDisplaySection from "./sections/TaxDisplaySection";
import TermsAndConditionsSection from "./sections/TermsAndConditionsSection";
import BuilderTemplatesSection from "./sections/BuilderTemplatesSection";  // iter 97.19 — Section 5

import { api, formatApiError } from "@/lib/api";

/**
 * Proposal Builder — proposal-scoped (Iter 97.9).
 *
 * Architectural redirect (operator brief 2026-02-27):
 *   The Builder is no longer an independent destination. It is launched
 *   from within an existing Proposal and is bound 1:1 to its Proposal ID.
 *   Each Proposal owns a single prototype keyed `proposal-{id}` in
 *   localStorage. Backend persistence remains out of scope this iteration;
 *   the Builder simply replaces/populates the Commercials section of the
 *   existing Proposal workflow.
 *
 *   Everything else in the production Proposal pipeline (Cover, T&C,
 *   Signature, PDF, Lifecycle, Approval) is untouched.
 */
export default function ProposalBuilderPrototype() {
    const navigate = useNavigate();
    const { id: proposalId } = useParams();
    const [searchParams] = useSearchParams();
    // 2026-07-22 · Proposal Versioning v2 — the Builder can be launched
    // pointed at any specific version via `?versionId=PVR-...`. When the
    // query param is absent we fall back to the latest active version
    // (see `targetVersion` below).
    const requestedVersionId = searchParams.get("versionId");

    const [proposal, setProposal] = useState(null);
    const [proposalLoading, setProposalLoading] = useState(true);
    const [prototype, setPrototype] = useState(null);
    const [savedFlash, setSavedFlash] = useState(false);
    const [applying, setApplying] = useState(false);
    // 2026-07-22 · Proposal Versioning v2 — the revision note the operator
    // provides when they save on a HISTORICAL version. Empty when we're
    // saving in-place on the latest active version.
    const [revisionNote, setRevisionNote] = useState("");

    /* Iter 97.11 — Catalogue hydration. The Builder reads the Products
       & Services master at runtime instead of from a hardcoded array.
       We subscribe to hydration state so the Builder shell can render a
       loading + error surface before mounting the Section 1 picker.

       Iter 97.18 (2026-02-27) — ALWAYS refetch on mount so newly added
       products in /catalogue appear without a hard reload. The legacy
       idle-only guard caused stale picker bug — operator added a "Test"
       product but it wasn't visible because the cached `ready` state
       blocked a refetch. */
    const [catalogueState, setCatalogueState] = useState(() => getHydrationState());
    useEffect(() => {
        const unsub = subscribeToHydration(setCatalogueState);
        hydrateProductMasterFromCatalogue({ force: true }).catch(() => { /* error captured via state */ });
        return unsub;
    }, []);

    const prototypeId = useMemo(() => proposalId ? `proposal-${proposalId}` : null, [proposalId]);

    /* 2026-07-22 · Proposal Versioning v2 — target version selection.
       - If the caller supplies `?versionId=PVR-...`, that exact version
         is the target.
       - Otherwise pick the latest ACTIVE (non-cancelled) version. This
         mirrors ProposalDetail's default so opening the Builder from any
         entry point Just Works.
       Non-Draft proposals still expose the Builder as read-only (Apply
       is disabled) so the operator can inspect but not persist. */
    const targetVersion = useMemo(() => {
        if (!proposal) return null;
        const versions = proposal.versions || [];
        if (requestedVersionId) {
            return versions.find((v) => v.id === requestedVersionId) || null;
        }
        const active = versions.filter((v) => (v.status || "active") !== "cancelled");
        const pool = active.length ? active : versions;
        return pool.length
            ? pool.reduce((a, b) => (a.version_number > b.version_number ? a : b))
            : null;
    }, [proposal, requestedVersionId]);

    // The latest ACTIVE version — used to decide whether Save flows
    // in-place (same as pre-v2) or spawns a new max+1 version.
    const latestActiveVersion = useMemo(() => {
        if (!proposal) return null;
        const active = (proposal.versions || []).filter(
            (v) => (v.status || "active") !== "cancelled"
        );
        return active.length
            ? active.reduce((a, b) => (a.version_number > b.version_number ? a : b))
            : null;
    }, [proposal]);

    // 2026-07-22 · Proposal Versioning v2 · post-UAT simplification —
    // Every Save creates the next version regardless of which version was
    // opened. `nextVersionNumber` is the number the next Save will
    // produce; used to label the Save button and the version banner.
    const nextVersionNumber = useMemo(() => {
        const versions = proposal?.versions || [];
        return versions.length
            ? Math.max(...versions.map((v) => v.version_number)) + 1
            : 1;
    }, [proposal]);

    // Everything below still reads from `editableVersion` for backward
    // compatibility with the existing Apply pipeline (line materialisation
    // + Special-Offer wiring). It resolves to `targetVersion` when the
    // proposal is Draft, otherwise `null` so the Apply button hides.
    const editableVersion = useMemo(() => {
        if (!proposal || proposal.status !== "Draft") return null;
        if (!targetVersion) return null;
        // A cancelled version can still be used as a source — save creates
        // the next version; the cancelled source itself stays untouched.
        return targetVersion;
    }, [proposal, targetVersion]);

    // Proposal is Draft-only editable. Once Approved/Sent/Converted/
    // Cancelled the Builder opens in read-only mode: inputs still render
    // (so the operator can inspect what was authored) but the Save
    // button is hidden and a locked banner appears.
    const isReadOnly = !editableVersion;

    /* ── Fetch proposal context (read-only header source of truth) ── */
    useEffect(() => {
        if (!proposalId) { setProposalLoading(false); return; }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/billing/proposal/${proposalId}`);
                if (!cancelled) setProposal(data);
            } catch (err) {
                if (!cancelled) toast.error(formatApiError(err, "Failed to load proposal"));
            } finally {
                if (!cancelled) setProposalLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [proposalId]);

    /* ── Load (or initialise) the prototype for the current target version ──
       2026-07-22 · Proposal Versioning v2 — the Builder is now
       version-scoped. Preference order:
         1. If the target version carries a persisted `builder_state`
            on the proposal document, use it verbatim (single source of
            truth across users / devices / sessions).
         2. Else fall back to per-version localStorage (`proposal-{id}-v-{versionId}`).
         3. Else fall back to legacy proposal-scoped localStorage
            (`proposal-{id}`) so pre-v2 drafts don't get discarded.
         4. 2026-07-23 HOTFIX (CB-02 restore) — Legacy proposals whose
            versions were persisted BEFORE `builder_state` existed
            still have `line_items` on the version. Reverse-materialise
            from `line_items` so the operator sees their existing
            products selected + priced. Requires PRODUCT_MASTER hydration
            so Master products / modules resolve to their catalogue
            entries; falls back to `custom` rows for unmatched names so
            nothing is lost.
         5. Else create a fresh default prototype seeded from the
            proposal header (client name / currency / number).
       This is what makes point #3 of the versioning spec work — opening
       V2 loads V2's exact snapshot, never leaks V6's state. */
    useEffect(() => {
        if (!prototypeId || !proposal || !targetVersion) return;
        const versionScopedId = `${prototypeId}-v-${targetVersion.id}`;
        // Base skeleton — used both as the fresh default and as a
        // safety-net when a persisted builder_state is missing top-level
        // keys (e.g. legacy or partial payloads from earlier iterations).
        const skeleton = defaultPrototype({
            id: versionScopedId,
            title: proposal.proposal_number || "Proposal",
            clientName: proposal.client?.name || "",
            currency: proposal.currency || "INR",
        });
        // 2026-07-23 HOTFIX — helper: is there real content in this
        // prototype? Empty scope = we should attempt reconstruction.
        const hasScope = (proto) => proto && Array.isArray(proto.scope) && proto.scope.length > 0;
        const hasLegacyLines = Array.isArray(targetVersion.line_items) && targetVersion.line_items.length > 0;
        const catalogueReady = catalogueState.state === "ready" && PRODUCT_MASTER.length > 0;

        let p = null;
        // (1) Server-side snapshot on this version. MERGED into the
        // skeleton so partial payloads don't crash sections that expect
        // `commercials`, `amc`, `taxDisplay`, `tnc` etc. to exist.
        if (targetVersion.builder_state) {
            const bs = targetVersion.builder_state;
            p = {
                ...skeleton,
                ...bs,
                id: versionScopedId,
                commercials: { ...skeleton.commercials, ...(bs.commercials || {}) },
                amc: { ...skeleton.amc, ...(bs.amc || {}) },
                taxDisplay: { ...skeleton.taxDisplay, ...(bs.taxDisplay || {}) },
                tnc: { ...skeleton.tnc, ...(bs.tnc || {}) },
                scope: Array.isArray(bs.scope) ? bs.scope : skeleton.scope,
            };
            // 2026-07-23 HOTFIX — Empty snapshot but line_items exist?
            // Discard the empty snapshot so reconstruction runs.
            if (!hasScope(p) && hasLegacyLines) p = null;
        }
        // (2) Per-version localStorage.
        if (!p) p = getPrototype(versionScopedId);
        // 2026-07-23 HOTFIX — stale-empty localStorage guard. Older
        // deploys wrote an empty defaultPrototype to localStorage the
        // FIRST time the operator opened the Builder on this version.
        // On the next mount the code below saw "p is truthy" and
        // skipped reconstruction — so the Builder stayed empty even
        // though the version has persisted line_items. Treat "prototype
        // exists but scope is empty AND version has line_items" as if
        // no prototype existed so reconstruction runs and overwrites
        // the stale cache. Fresh versions with intentionally empty
        // scope short-circuit into the default-prototype branch.
        if (p && !hasScope(p) && hasLegacyLines) p = null;
        // (3) Legacy proposal-scoped localStorage (only for the LATEST
        //     active version — historical versions must NEVER inherit
        //     a stale global blob).
        if (!p && targetVersion.id === latestActiveVersion?.id) {
            const legacy = getPrototype(prototypeId);
            if (legacy && (hasScope(legacy) || !hasLegacyLines)) {
                p = { ...legacy, id: versionScopedId };
            }
        }
        // (4) 2026-07-23 HOTFIX — Reverse-materialise from persisted
        // line_items (legacy proposals populated before builder_state
        // existed). Guarded on catalogue readiness so Master products
        // and modules resolve correctly. If the catalogue is still
        // loading, defer — this effect re-runs when catalogueState flips.
        if (!p && hasLegacyLines) {
            if (!catalogueReady) return;
            p = reverseMaterializeFromLineItems(targetVersion.line_items, {
                prototypeId: versionScopedId,
                title: proposal.proposal_number || "Proposal",
                clientName: proposal.client?.name || "",
                currency: proposal.currency || "INR",
            });
        }
        // (5) Fresh default (brand-new version with no line_items).
        if (!p) p = skeleton;
        // Currency on the Proposal is always the source of truth.
        if ((proposal.currency || "INR") !== p.currency) {
            p = { ...p, currency: proposal.currency || "INR" };
        }
        upsertPrototype(p);
        setPrototype(p);
        setRevisionNote("");
    }, [prototypeId, proposal, targetVersion, latestActiveVersion, catalogueState.state]);

    /* ── Auto-save on every change (debounced via micro-task) ── */
    useEffect(() => {
        if (!prototype) return;
        upsertPrototype(prototype);
        setSavedFlash(true);
        const t = setTimeout(() => setSavedFlash(false), 800);
        return () => clearTimeout(t);
         
    }, [JSON.stringify(prototype || {})]);

    function patchPrototype(patch) {
        setPrototype((prev) => ({ ...(prev || {}), ...patch }));
    }

    /* Iter 97.10 — "Save / Apply to Proposal" handler.
       Materialises the Builder draft into ProposalLineIn[] and replaces
       the proposal version's line_items via the new apply endpoint.
       The rich Builder localStorage state is preserved untouched so the
       operator can keep refining. */
    async function handleApplyToProposal() {
        if (!proposalId || !prototype) return;
        if (!editableVersion) {
            toast.error(`Cannot apply: proposal status is "${proposal?.status}". Only Draft proposals can be edited.`);
            return;
        }
        const gaps = annualCostGaps(prototype);
        if (gaps.length > 0) {
            toast.error(`Enter an Annual Cost or mark it Complimentary for: ${gaps.map((r) => r.moduleLabel || r.productName).join(", ")}`);
            return;
        }
        const products = aggregateScopeByProduct(prototype.scope || []);
        const lineItems = materializeBuilderToLineItems(prototype, products);
        if (lineItems.length === 0) {
            toast.error("Add at least one product with a price before applying to the Proposal.");
            return;
        }

        /* Iter 97.20 — Builder.discount → proposal.special_offer.
           When the operator entered a non-zero discount with required
           metadata (label + reasonCode), serialize it so the backend
           upserts the offer on the proposal. When the operator zeroed
           out a previously-set discount, send enabled=false so the
           backend clears any existing offer. Otherwise omit the field
           entirely — legacy proposals never see this branch. */
        const d = prototype.commercials?.discount || {};
        const discountValue = Number(d.value) || 0;
        const proposalHasExistingOffer = !!proposal?.special_offer;
        const offerLabel = (d.label || d.reason || "").trim();
        const offerReason = d.reasonCode || null;
        // UAT 45.6.6 (2026-07-14) — Standardized offer taxonomy fields.
        const offerType = d.offerType || null;
        const offerDescription = (d.description || "").trim() || null;

        let specialOfferPayload = null;
        let offerValidationError = null;
        if (discountValue > 0) {
            if (!offerLabel) {
                offerValidationError = "Add an Offer label before applying the Special Discount (required for PDF).";
            } else if (!offerReason) {
                offerValidationError = "Pick a Reason code for the Special Discount before applying.";
            } else if (offerType === "CUSTOM" && !offerDescription) {
                offerValidationError = "Custom Discount requires a customer-facing Description.";
            } else {
                specialOfferPayload = {
                    enabled: true,
                    label: offerLabel,
                    discount_type: d.type === "fixed" ? "FixedAmount" : "Percentage",
                    discount_value: discountValue,
                    reason_code: offerReason,
                    offer_type: offerType,
                    description: offerDescription,
                };
            }
        } else if (proposalHasExistingOffer) {
            specialOfferPayload = { enabled: false };
        }
        if (offerValidationError) {
            toast.error(offerValidationError);
            return;
        }

        // If we're about to overwrite an existing offer, confirm explicitly.
        if (specialOfferPayload?.enabled && proposalHasExistingOffer) {
            const existing = proposal.special_offer;
            const ok = window.confirm(
                `This will REPLACE the existing Special Offer on ${proposal.proposal_number}:\n\n` +
                `  Existing: ${existing.label} (${existing.discount_type} ${existing.discount_value})\n` +
                `  New     : ${specialOfferPayload.label} (${specialOfferPayload.discount_type} ${specialOfferPayload.discount_value})\n\n` +
                `Proceed?`,
            );
            if (!ok) return;
        }
        if (specialOfferPayload && !specialOfferPayload.enabled && proposalHasExistingOffer) {
            const ok = window.confirm(
                `This will CLEAR the existing Special Offer on ${proposal.proposal_number} (because the Builder discount is 0). Proceed?`,
            );
            if (!ok) return;
        }

        setApplying(true);
        try {
            // 2026-07-23 HOTFIX — strip reconstruction marker flags
            // (`_recovered`, `_recoveredAt`) from the persisted
            // `builder_state`. They exist only to help the UI show a
            // hint after reverse-materialisation; the backend has no
            // schema for them and they must not appear on V{next}.
            const { _recovered: _rec, _recoveredAt: _recAt, ...cleanPrototype } = prototype;
            const payload = {
                version_id: editableVersion.id,
                line_items: lineItems,
                presentation_mode: prototype.commercials?.presentationMode || "itemized",
                // 2026-07-22 · Proposal Versioning v2 — persist the full
                // Builder blob per version so re-opening the newly-created
                // version later hydrates the exact authored state
                // (products, hierarchy, setup fee, AMC, GST, discounts,
                // delivery modes, scope, T&C block, milestones).
                builder_state: cleanPrototype,
                // Optional per-save note; backend auto-fills when empty.
                revision_note: revisionNote.trim() || undefined,
            };
            if (specialOfferPayload) payload.special_offer = specialOfferPayload;
            // PB-004 (V1-4.T&C) — Persist Section 4 T&C block on apply.
            // Local Builder state uses camelCase; backend contract uses
            // snake_case. Translate here so the API surface stays canonical.
            const t = prototype.tnc || {};
            payload.tnc = {
                mode: t.mode || "full",
                reference_proposal_number: t.referenceProposalNumber || null,
                reference_note: t.referenceNote || null,
                additional_clauses: (t.additionalClauses || []).map((c) => ({
                    title: c.title || "",
                    body: c.body || "",
                })),
            };
            // 2026-07-22 · Proposal Versioning v2 · post-UAT simplification —
            // Every Save creates the next version. The historical-edit
            // branch was retired: the backend now always creates a new
            // max+1 version regardless of which version the operator
            // opened. Mental model = V1 → Save → V2 → Save → V3.

            const { data: applyRes } = await api.post(
                `/billing/proposal/${proposalId}/commercials-builder/apply`,
                payload,
            );
            toast.success(
                `Saved as V${applyRes.version_number}` +
                (applyRes.source_version_no
                    ? ` (from V${applyRes.source_version_no})`
                    : ""),
            );
            navigate(`/proposals/${proposalId}`);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to apply Commercials Builder output"));
        } finally {
            setApplying(false);
        }
    }

    /* ── Summary metrics ── */
    const summary = useMemo(() => {
        if (!prototype) return null;
        const productCount = (prototype.scope || []).length;
        const m = prototype.commercials || {};
        const setupTotal = Object.values(m.itemPrices || {}).reduce((s, v) => s + (Number(v) || 0), 0);
        const amcTotal = (prototype.amc?.items || []).reduce((s, a) => s + (Number(a.amount) || 0), 0);
        const annualRows = (prototype.scope || []).filter((r) => r.recurring_kind === "annual_cost");
        const annualCostTotal = annualRows.reduce((s, r) => s + (r.annual_cost_pricing_mode === "complimentary"
            ? 0 : (Number(m.annualCostPrices?.[r.id]) || 0)), 0);
        return { productCount, setupTotal, amcTotal, annualCostTotal, anyAnnualCost: annualRows.length > 0 };
    }, [prototype]);

    function formatMoney(n) {
        if (typeof n !== "number" || isNaN(n)) return "—";
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: prototype?.currency || "INR",
            maximumFractionDigits: 0,
        }).format(n);
    }

    function formatProposalDate(iso) {
        if (!iso) return "—";
        try {
            return new Date(iso).toLocaleDateString("en-IN", {
                day: "2-digit", month: "short", year: "numeric",
            });
        } catch { return "—"; }
    }

    if (proposalLoading) {
        return <div className="p-8 text-slate-500" data-testid="builder-loading">Loading proposal…</div>;
    }
    if (!proposal) {
        return (
            <div className="p-8 text-slate-500" data-testid="builder-proposal-missing">
                Proposal not found.{" "}
                <Button variant="link" onClick={() => navigate("/proposals")}>Back to Proposals</Button>
            </div>
        );
    }
    if (!prototype) {
        return <div className="p-8 text-slate-500" data-testid="builder-loading">Preparing builder…</div>;
    }
    /* Iter 97.11 — Catalogue must be hydrated before the picker mounts.
       The catalogue is the single master of Products in 97.11+ — there
       is no client-side fallback. */
    if (catalogueState.state === "loading" || catalogueState.state === "idle") {
        return <div className="p-8 text-slate-500" data-testid="catalogue-loading">Loading Products &amp; Services catalogue…</div>;
    }
    if (catalogueState.state === "error") {
        return (
            <div className="p-8 text-slate-700" data-testid="catalogue-error">
                <div className="font-semibold mb-1">Failed to load Products &amp; Services catalogue.</div>
                <div className="text-sm text-slate-500 mb-3">{catalogueState.error?.message || "Please retry."}</div>
                <Button size="sm" variant="outline" onClick={() => hydrateProductMasterFromCatalogue({ force: true })}>Retry</Button>
            </div>
        );
    }
    if (PRODUCT_MASTER.length === 0) {
        return (
            <div className="p-8 text-slate-700" data-testid="catalogue-empty">
                <div className="font-semibold mb-1">No active Products are configured in the catalogue.</div>
                <div className="text-sm text-slate-500 mb-3">An administrator must mark Products as active in <span className="font-mono">Products &amp; Services</span>.</div>
                <Button size="sm" variant="outline" onClick={() => navigate("/catalogue")}>Open Products &amp; Services</Button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 px-4 sm:px-6 lg:px-8 py-6" data-testid="proposal-builder-prototype-root">
            {/* Sticky header */}
            <div className="sticky top-0 z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 bg-white border-b border-slate-200 pt-4 pb-3 mb-6">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                        <Notebook size={28} weight="duotone" className="text-indigo-600" />
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h1 className="text-xl font-bold tracking-tight truncate" data-testid="page-title">
                                    Proposal Builder
                                </h1>
                                {savedFlash && (
                                    <span className="text-[11px] text-emerald-700 animate-pulse">Saved</span>
                                )}
                            </div>
                            <div className="text-xs text-slate-500">
                                Editing the Commercials section of an existing Proposal.
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/proposals/${proposalId}`)}
                            data-testid="back-to-proposal-btn"
                        >
                            <ArrowLeft className="mr-1" /> Back to Proposal
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/proposals/${proposalId}/builder/preview${targetVersion ? `?v=${targetVersion.id}` : ""}`)}
                            data-testid="header-preview-btn"
                        >
                            <ArrowSquareOut className="mr-1" /> Preview Commercials
                        </Button>
                        {!isReadOnly && (
                            <Button
                                size="sm"
                                onClick={handleApplyToProposal}
                                disabled={applying}
                                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                                data-testid="apply-to-proposal-btn"
                                title={`Saving will create V${nextVersionNumber} as the next version. Earlier versions stay untouched in Version History.`}
                            >
                                <FloppyDisk className="mr-1" weight="fill" />
                                {applying ? "Saving…" : `Save · creates V${nextVersionNumber}`}
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto space-y-6">
                {/* 2026-07-22 · Proposal Versioning v2 · post-UAT simplification —
                    Two banners collapsed into one contextual card at the top:
                      (a) READ-ONLY: proposal is Approved / Sent / Converted /
                          Cancelled → Builder is locked, no Save button.
                      (b) EDITABLE: proposal is Draft → shows "Editing V{n}.
                          Saving will create V{next} as the next version" +
                          optional revision note. Every Save creates the next
                          version; the historical/amber branch was retired. */}
                {targetVersion && isReadOnly && (
                    <div
                        className="reedos-card p-4 border border-slate-300 bg-slate-100"
                        data-testid="builder-readonly-banner"
                    >
                        <div className="flex items-center gap-3 flex-wrap">
                            <div className="flex-1 min-w-0">
                                <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">
                                    Read-only · Locked
                                </div>
                                <div className="text-sm font-semibold text-slate-900 mt-0.5 flex items-center gap-2 flex-wrap">
                                    <span className="font-mono">V{targetVersion.version_number}</span>
                                    <span className="text-slate-400">·</span>
                                    <span className="text-slate-700">
                                        proposal is <em>{proposal.status}</em>
                                    </span>
                                    {targetVersion.id === proposal.approved_version_id && (
                                        <span className="inline-flex items-center text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded bg-emerald-200 text-emerald-900">
                                            APPROVED
                                        </span>
                                    )}
                                </div>
                                <div className="text-xs text-slate-600 mt-1.5 leading-relaxed">
                                    {proposal.status === "Approved"
                                        ? "This proposal has been Approved and is locked. The approved version is the final commercial snapshot — no further editing or version creation is possible."
                                        : proposal.status === "Converted"
                                            ? "This proposal has been converted to a Purchase Order and is locked."
                                            : proposal.status === "Cancelled"
                                                ? "This proposal has been Cancelled and is locked."
                                                : proposal.status === "Rejected"
                                                    ? "This proposal has been Rejected and is locked."
                                                    : "This proposal is not in Draft status. Editing is disabled."}
                                </div>
                            </div>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/proposals/${proposalId}`)}
                                data-testid="builder-readonly-back-btn"
                            >
                                Back to Proposal
                            </Button>
                        </div>
                    </div>
                )}
                {targetVersion && !isReadOnly && (
                    <div
                        className="reedos-card p-4 border border-slate-200 bg-slate-50/50"
                        data-testid="builder-version-banner"
                    >
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="flex-1 min-w-0">
                                <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">
                                    Editing version
                                </div>
                                <div className="text-sm font-semibold text-slate-900 mt-0.5 flex items-center gap-2 flex-wrap">
                                    <span className="font-mono">V{targetVersion.version_number}</span>
                                    <span className="text-slate-400">·</span>
                                    <span className="text-slate-700">
                                        created {formatProposalDate(targetVersion.created_date)}
                                    </span>
                                    {targetVersion.source_version_no != null && (
                                        <>
                                            <span className="text-slate-400">·</span>
                                            <span className="text-slate-700">from V{targetVersion.source_version_no}</span>
                                        </>
                                    )}
                                    {latestActiveVersion && targetVersion.id === latestActiveVersion.id && (
                                        <span className="inline-flex items-center text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded bg-slate-900 text-white">
                                            LATEST
                                        </span>
                                    )}
                                </div>
                                <div className="text-xs text-slate-600 mt-1.5 leading-relaxed">
                                    Saving will create <strong>V{nextVersionNumber}</strong> as the next version.
                                    V{targetVersion.version_number} will remain unchanged in Version History.
                                </div>
                            </div>
                            <div className="w-full sm:w-96">
                                <label className="text-[10px] uppercase tracking-wide text-slate-500">
                                    Revision note (optional)
                                </label>
                                <input
                                    type="text"
                                    value={revisionNote}
                                    onChange={(e) => setRevisionNote(e.target.value)}
                                    placeholder={`e.g. Bumped AMC to 15% — V${nextVersionNumber}`}
                                    className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-1.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                                    data-testid="builder-revision-note-input"
                                />
                            </div>
                        </div>
                    </div>
                )}
                {/* Proposal context — read-only header (Iter 97.9) */}
                <Card className="border-slate-200" data-testid="proposal-context-card">
                    <CardContent className="pt-5 pb-5">
                        <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400 mb-2">
                            Proposal context
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-5">
                            <div>
                                <div className="text-[10px] uppercase tracking-wide text-slate-500">Client</div>
                                <div className="text-sm font-semibold text-slate-900 truncate" data-testid="ctx-client-name">
                                    {proposal.client?.name || "—"}
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wide text-slate-500">Proposal #</div>
                                <div className="text-sm font-semibold text-slate-900 font-mono truncate" data-testid="ctx-proposal-number">
                                    {proposal.proposal_number || "—"}
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wide text-slate-500">Proposal Date</div>
                                <div className="text-sm font-semibold text-slate-900" data-testid="ctx-proposal-date">
                                    {formatProposalDate(proposal.created_date)}
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wide text-slate-500">Currency</div>
                                <div className="text-sm font-semibold text-slate-900 font-mono" data-testid="ctx-currency">
                                    {proposal.currency || "INR"}
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wide text-slate-500">Status</div>
                                <div className="text-sm font-semibold text-slate-900" data-testid="ctx-status">
                                    {proposal.status || "—"}
                                </div>
                            </div>
                        </div>
                        {summary && (
                            <div className={`grid gap-2 mt-4 ${summary.anyAnnualCost ? "grid-cols-4" : "grid-cols-3"}`}>
                                <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                                    <div className="text-[10px] uppercase tracking-wide text-slate-500">Products in scope</div>
                                    <div className="text-lg font-bold" data-testid="summary-product-count">{summary.productCount}</div>
                                </div>
                                <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                                    <div className="text-[10px] uppercase tracking-wide text-slate-500">Setup Fee total</div>
                                    <div className="text-lg font-bold" data-testid="summary-setup-total">{formatMoney(summary.setupTotal)}</div>
                                </div>
                                <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                                    <div className="text-[10px] uppercase tracking-wide text-slate-500">Annual AMC total</div>
                                    <div className="text-lg font-bold" data-testid="summary-amc-total">{formatMoney(summary.amcTotal)}</div>
                                </div>
                                {summary.anyAnnualCost && (
                                    <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                                        <div className="text-[10px] uppercase tracking-wide text-slate-500">Annual Cost total</div>
                                        <div className="text-lg font-bold" data-testid="summary-annual-cost-total">{formatMoney(summary.annualCostTotal)}</div>
                                    </div>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Sections (business logic UNCHANGED — only the surrounding shell was refactored).
                    2026-07-22 · post-UAT — When the proposal is not Draft the entire
                    section stack is wrapped in a pointer-events-none, muted container so
                    the operator can inspect but not modify. All local state changes are
                    also swallowed by ignoring the onChange handlers via `patchPrototype`
                    being a no-op in read-only mode below. */}
                <div
                    className={isReadOnly ? "pointer-events-none opacity-75 select-none" : ""}
                    aria-disabled={isReadOnly}
                    data-testid="builder-sections-wrap"
                >
                    <ProductSelectionSection
                        scope={prototype.scope}
                        prototype={prototype}
                        onChange={(scope) => !isReadOnly && patchPrototype({ scope })}
                    />
                    <CommercialSection
                        scope={prototype.scope}
                        commercials={prototype.commercials}
                        amc={prototype.amc}
                        currency={prototype.currency}
                        onChange={(commercials) => !isReadOnly && patchPrototype({ commercials })}
                        onAmcChange={(amc) => !isReadOnly && patchPrototype({ amc })}
                        onCurrencyChange={(currency) => !isReadOnly && patchPrototype({ currency })}
                        onScopeChange={(scope) => !isReadOnly && patchPrototype({ scope })}
                    />
                    <TaxDisplaySection
                        taxDisplay={prototype.taxDisplay}
                        onChange={(taxDisplay) => !isReadOnly && patchPrototype({ taxDisplay })}
                    />
                    <TermsAndConditionsSection
                        tnc={prototype.tnc}
                        onChange={(tnc) => !isReadOnly && patchPrototype({ tnc })}
                    />

                    {/* Iter 97.19 — Section 5: Backend-persisted templates +
                        preview. Operators save/apply Builder configurations
                        across proposals. */}
                    <BuilderTemplatesSection
                        prototype={prototype}
                        proposal={proposal}
                        onApplyToBuilder={(nextPrototype) => !isReadOnly && setPrototype(nextPrototype)}
                    />
                </div>

                <div className="text-center text-xs text-slate-500 italic py-4">
                    Local-only · state persists in your browser&apos;s localStorage, bound to Proposal&nbsp;
                    <span className="font-mono">{proposal.proposal_number}</span>. Final PDF is generated from the production REEDOS proposal template.
                </div>
            </div>
        </div>
    );
}
